/**
 * ServiceDetailPanel — unified detail card (TASK-197, contract revised by
 * d2fa004). Moved out of ServiceManagementPage.tsx (KAN-157 god-component
 * split).
 *
 * The detail panel (data-testid="service-detail-panel") renders, after the
 * header (name <h3> + type badge + «с подуслугами» badge), the POPULATED
 * subset of these 5 sections, always in this order, for every service type:
 *
 *   1. Входные параметры   data-testid="detail-section-inputs"
 *   2. Расчёт              data-testid="detail-section-calculation"
 *   3. Постобработка       data-testid="detail-section-postprocessing"
 *   4. Выходные данные     data-testid="detail-section-output"
 *   5. Подуслуги           data-testid="detail-section-subservices"
 *
 * Each section heading is an <h4> (the service name stays in <h3>). An EMPTY
 * section is NOT rendered at all — EXCEPT «Расчёт», which is always rendered
 * (isEmpty={false} below) and shows an amber `CalcEmptyNotice` warning instead
 * of vanishing when a leaf service has no calc data (KAN-123). See
 * ServiceManagementPage.tsx's file header for the full history/trade-offs of
 * this contract.
 */
import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Service } from '@/services/api/services';
import {
  clientInputLines,
  outputInfo,
  pricingItems,
  questionnaireInputLines,
  serviceTypeLabel,
  serviceTypeVariant,
} from '@/utils/serviceFormula';
import { CalculatorDetails } from './CalculatorDetails';
import { ClassifierDetails } from './ClassifierDetails';
import { ClientInputList } from './ClientInputList';
import { DetailSection } from './DetailSection';
import { ModifierDetails } from './ModifierDetails';
import { PricelistDetails, PricelistInputs } from './PricelistDetails';
import { QuestionnaireDetails } from './QuestionnaireDetails';
import { SubservicesSection } from './SubservicesSection';

export function ServiceDetailPanel({
  service,
  childrenByParent,
  onSelect,
  onEdit,
  onDelete,
}: {
  service: Service;
  childrenByParent: Map<number, Service[]>;
  onSelect: (service: Service) => void;
  onEdit: (service: Service) => void;
  onDelete: (service: Service) => void;
}): JSX.Element {
  // TASK-197 — the detail card ALWAYS renders the same 5 sections, in order.
  // Each section's content + emptiness is decided HERE by explicit booleans
  // (never by inspecting React.Children) and handed to <DetailSection>.
  const chains = service.chains || [];
  const hasChain = chains.length > 0;
  const children = childrenByParent.get(service.id) || [];
  const hasChildren = children.length > 0;
  const method = service.calculation_method;

  // ── 1. Входные параметры — client-facing inputs only ────────────────────
  // The pricelist case is rendered SPECIALLY: PricelistInputs already carries
  // the «Входные параметры» heading inside its pricelist-inputs div (TASK-192),
  // so the inputs section must NOT add a second heading. `pricelistInputs`
  // flags that path; otherwise a generic DetailSection (with its own <h4>) is
  // used for calculator/classifier inputs.
  const pricelistInputs = method === 'pricelist' && pricingItems(service).length > 0;
  let inputsContent: JSX.Element | null = null;
  let inputsEmpty = true;
  if (method === 'pricelist') {
    // Per-position «количество '<name>' (<unit>)» lines (TASK-192).
    if (pricingItems(service).length > 0) {
      inputsContent = <PricelistInputs service={service} />;
      inputsEmpty = false;
    }
  } else if (method === 'classifier') {
    // Classifier client inputs come ONLY from a modifier chain's user params.
    // A classifier WITHOUT a chain has no client inputs → section hidden.
    const lines = hasChain ? clientInputLines(chains[chains.length - 1]) : [];
    if (lines.length > 0) {
      inputsContent = <ClientInputList lines={lines} />;
      inputsEmpty = false;
    }
  } else if (method === 'calculator') {
    // Calculator inputs come from all of the service's chains.
    const lines = chains.flatMap(c => clientInputLines(c));
    if (lines.length > 0) {
      inputsContent = <ClientInputList lines={lines} />;
      inputsEmpty = false;
    }
  } else if (method === 'questionnaire') {
    // Questionnaire inputs are the questions themselves (TASK-332) —
    // built from questionnaire_fields, a questionnaire has NO chain.
    const lines = questionnaireInputLines(service);
    if (lines.length > 0) {
      inputsContent = <ClientInputList lines={lines} />;
      inputsEmpty = false;
    }
  }

  // ── 2. Расчёт — the main body (always populated for our fixtures) ────────
  let calcContent: JSX.Element;
  if (method === 'pricelist') {
    calcContent = <PricelistDetails service={service} />;
  } else if (method === 'classifier') {
    calcContent = <ClassifierDetails service={service} />;
  } else if (method === 'calculator') {
    calcContent = <CalculatorDetails service={service} />;
  } else if (method === 'questionnaire') {
    // TASK-332: questions table instead of formulas/positions.
    calcContent = <QuestionnaireDetails service={service} />;
  } else if (hasChildren) {
    // Parent without a calc method (edge): nothing to render in Расчёт.
    calcContent = <p className="text-sm text-gray-500">Стоимость складывается из подуслуг.</p>;
  } else {
    calcContent = <p className="text-sm text-gray-500">Справочная услуга — без расчётных данных.</p>;
  }

  // ── 3. Постобработка — modifier overlay ONLY for pricelist/classifier ───
  const hasPostprocessing = hasChain && (method === 'pricelist' || method === 'classifier');

  // ── 4. Выходные данные — output name/unit from the relevant chain ───────
  const output = outputInfo(service);

  return (
    <div data-testid="service-detail-panel" className="rounded-lg border bg-white shadow-sm p-4 space-y-4">
      {/* Header: name in <h3> (so getByRole('heading', { name }) resolves),
          real-type badge + «с подуслугами» marker (TASK-185) + Edit/Delete. */}
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-semibold text-gray-900">{service.name}</h3>
        <Badge variant={serviceTypeVariant(service)}>{serviceTypeLabel(service)}</Badge>
        {service.is_aggregator && <Badge variant="outline">с подуслугами</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <Button
            data-testid="edit-service-button"
            variant="outline"
            size="sm"
            onClick={() => onEdit(service)}>
            <Pencil className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Редактировать</span>
          </Button>
          <Button
            data-testid="delete-service-button"
            variant="destructive"
            size="sm"
            onClick={() => onDelete(service)}>
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Удалить</span>
          </Button>
        </div>
      </div>

      {/* 1. Входные параметры */}
      {pricelistInputs ? (
        // Pricelist: PricelistInputs already supplies the «Входные параметры»
        // <h4> inside its pricelist-inputs div, so we don't add another one.
        <div data-testid="detail-section-inputs">{inputsContent}</div>
      ) : (
        <DetailSection title="Входные параметры" testid="detail-section-inputs" isEmpty={inputsEmpty}>
          {inputsContent}
        </DetailSection>
      )}

      {/* 2. Расчёт */}
      <DetailSection title="Расчёт" testid="detail-section-calculation" isEmpty={false}>
        {calcContent}
      </DetailSection>

      {/* 3. Постобработка */}
      <DetailSection
        title="Постобработка"
        testid="detail-section-postprocessing"
        isEmpty={!hasPostprocessing}>
        {hasPostprocessing && <ModifierDetails service={service} />}
      </DetailSection>

      {/* 4. Выходные данные */}
      <DetailSection title="Выходные данные" testid="detail-section-output" isEmpty={output === null}>
        {output !== null && <p className="text-sm text-gray-700">{output}</p>}
      </DetailSection>

      {/* 5. Подуслуги */}
      <DetailSection title="Подуслуги" testid="detail-section-subservices" isEmpty={!hasChildren}>
        {hasChildren && (
          <SubservicesSection service={service} childrenByParent={childrenByParent} onSelect={onSelect} />
        )}
      </DetailSection>
    </div>
  );
}
